import React, { useState } from "react";
import { Button, Spinner } from "react-bootstrap";
import { toast } from "react-toastify";
import DropZone from "../../ui/DropZone";
import { BASE_URL } from "../../../data/BaseUrl";
import { importRawLead, sampleRawLeadCSV } from "../../../data/ApiEndpoints";

const ImportRawLeads = ({ userInfoData, closeModal }) => {
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const onDropFile = (acceptedFiles) => {
    if (acceptedFiles && acceptedFiles.length > 0) {
      setFile(acceptedFiles[0]);
      setResult(null);
    }
  };

  const onClickUpload = async () => {
    if (!file) {
      toast.error("Please select a CSV file first");
      return;
    }

    setLoading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);

      const url = BASE_URL + importRawLead;
      const response = await fetch(url, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${userInfoData?.token}`,
        },
        body: formData,
      });

      const data = await response.json();

      if (data.status) {
        toast.success(data.message);
        setResult(data.data);
      } else {
        toast.error(data.message || "Import failed");
      }
    } catch (error) {
      toast.error("Something went wrong during import");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const onClickDownloadSample = async () => {
    try {
      const url = BASE_URL + sampleRawLeadCSV;
      const response = await fetch(url, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${userInfoData?.token}`,
        },
      });

      if (!response.ok) {
        toast.error("Failed to download sample CSV");
        return;
      }

      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = downloadUrl;
      link.download = "sample_raw_leads.csv";
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(downloadUrl);
    } catch (error) {
      toast.error("Failed to download sample CSV");
      console.error(error);
    }
  };

  return (
    <div className="p-3">
      <div className="mb-3 d-flex justify-content-end">
        <Button
          variant="outline-danger"
          size="sm"
          onClick={onClickDownloadSample}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="14"
            height="14"
            fill="currentColor"
            className="bi bi-download me-1"
            viewBox="0 0 16 16"
          >
            <path d="M.5 9.9a.5.5 0 0 1 .5.5v2.5a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-2.5a.5.5 0 0 1 1 0v2.5a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2v-2.5a.5.5 0 0 1 .5-.5" />
            <path d="M7.646 11.854a.5.5 0 0 0 .708 0l3-3a.5.5 0 0 0-.708-.708L8.5 10.293V1.5a.5.5 0 0 0-1 0v8.793L5.354 8.146a.5.5 0 1 0-.708.708z" />
          </svg>
          Download Sample CSV
        </Button>
      </div>

      <DropZone
        onDrop={onDropFile}
        accept={{
          "text/csv": [".csv"],
          "application/vnd.ms-excel": [".xls"],
          "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [".xlsx"],
        }}
        multiple={false}
        maxSize={10485760}
        message="Drag & drop your CSV/Excel file here, or click to select"
      />

      <div className="d-flex justify-content-end mt-3 gap-2">
        <Button variant="outline-secondary" onClick={closeModal}>
          Cancel
        </Button>
        <Button
          variant="danger"
          onClick={onClickUpload}
          disabled={!file || loading}
        >
          {loading ? (
            <>
              <Spinner animation="border" size="sm" className="me-1" />
              Uploading...
            </>
          ) : (
            "Upload & Import"
          )}
        </Button>
      </div>

      {result && (
        <div className="mt-3 p-3" style={{ background: "#f8f9fa", borderRadius: "8px" }}>
          <h6>Import Results</h6>
          <p className="mb-1">
            <strong>Total Rows:</strong> {result.total}
          </p>
          <p className="mb-1" style={{ color: "#28a745" }}>
            <strong>Successfully Added:</strong> {result.inserted}
          </p>
          <p className="mb-1" style={{ color: "#dc3545" }}>
            <strong>Skipped:</strong> {result.skipped}
          </p>
          {result.errors && result.errors.length > 0 && (
            <div className="mt-2">
              <strong>Error Details:</strong>
              <div
                style={{
                  maxHeight: "150px",
                  overflowY: "auto",
                  fontSize: "13px",
                  marginTop: "5px",
                }}
              >
                {result.errors.map((err, idx) => (
                  <p key={idx} className="mb-1 text-danger">
                    Row {err.row}: {err.reason}
                    {err.mobile ? ` (${err.mobile})` : ""}
                  </p>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ImportRawLeads;
